const STORAGE_KEY = 'playfynd_location_search_history';
const MAX_ITEMS = 40;

const roundCoord = (n) => Math.round(Number(n) * 1e5) / 1e5;

/**
 * Stable key for deduplicating saved / suggested places.
 */
export const locationHistoryDedupeKey = (item) => {
  if (item.placeId) return `p:${item.placeId}`;
  if (item.lat != null && item.lng != null) {
    return `c:${roundCoord(item.lat)},${roundCoord(item.lng)}`;
  }
  const main = (item.mainText || item.name || '').trim().toLowerCase();
  const sub = (item.secondaryText || item.description || '').trim().toLowerCase();
  return `t:${main}|${sub}`;
};

const readList = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const writeList = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch {
    /* quota */
  }
};

/**
 * Save or bump a place to the front of recent searches (localStorage).
 * @param {{ mainText: string, secondaryText?: string, lat?: number, lng?: number, placeId?: string }} item
 */
export const addLocationSearchHistory = (item) => {
  const mainText = (item.mainText || item.name || '').trim();
  if (!mainText) return;

  const entry = {
    mainText,
    secondaryText: (item.secondaryText || item.description || '').trim(),
    lat: item.lat != null ? Number(item.lat) : undefined,
    lng: item.lng != null ? Number(item.lng) : undefined,
    placeId: (item.placeId || '').trim(),
    ts: Date.now(),
  };

  const key = locationHistoryDedupeKey(entry);
  const list = readList().filter((x) => locationHistoryDedupeKey(x) !== key);
  list.unshift(entry);
  writeList(list);
};

/**
 * Recent entries whose label matches the query (substring, case-insensitive).
 * Newest first (read order preserved after unshift dedupe).
 */
export const matchLocationSearchHistory = (query) => {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  return readList().filter((x) => {
    const hay = `${x.mainText} ${x.secondaryText || ''}`.toLowerCase();
    return hay.includes(q);
  });
};

const normApiRow = (row) => ({
  mainText: row.mainText ?? row.name ?? '',
  secondaryText: row.secondaryText ?? row.description ?? '',
  placeId: row.placeId || '',
  lat: row.lat,
  lng: row.lng,
});

/**
 * Up to `maxTotal` rows: matching history first, then API predictions (no duplicates).
 */
export const mergeHistoryWithApiPredictions = (historyRows, apiRows, maxTotal = 5) => {
  const seen = new Set();
  const out = [];

  const push = (row, isHistory) => {
    if (out.length >= maxTotal) return;
    const n = normApiRow(row);
    const k = locationHistoryDedupeKey(n);
    if (seen.has(k)) return;
    seen.add(k);
    out.push({
      ...n,
      isHistory,
      listKey: `${isHistory ? 'h' : 'a'}-${k}`,
    });
  };

  for (const h of historyRows) push(h, true);
  for (const p of apiRows) push(p, false);
  return out;
};
